import {
  AppInsightsContext,
  Modal,
  ResponsiveImage,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  UserContext,
} from '@/components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import ImageIcon from '@/assets/imageIcon.svg';
import { useContext, useMemo, useState } from 'react';
import XLight from '@/assets/xLight.svg';
import EyeIcon from '@/assets/eye-light.svg';
import DownloadSimpleLight from '@/assets/downloadSimpleLight.svg';
import ReportButton from '../common/ReportButton';
import { useTranslations } from 'next-intl';
import { ReportListCardModel } from './ReportListCard';
import { getGlobalDataPdf } from '@/api';

const ReportListCardGD = ({
  data,
  className,
  small,
  onClick,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hideViewed = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showActionButtons,
  ...props
}: ReportListCardModel) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  const t = useTranslations();

  const user = useContext(UserContext);
  const appInsights = useContext(AppInsightsContext);

  const isImage = useMemo(() => !!data.image?.imageUrl, [data.image]);

  const imageSize = useMemo(() => {
    let widths: { [x: string]: number } = {};
    let heights: { [x: string]: number } = {};

    if (small) {
      widths = { xs: 80, sm: 80, md: 80, lg: 80, xl: 80 };
      heights = { xs: 68, sm: 68, md: 68, lg: 68, xl: 68 };
    } else {
      widths = { xs: 176, sm: 176, md: 176, lg: 176, xl: 176 };
      heights = { xs: 152, sm: 152, md: 152, lg: 152, xl: 152 };
    }

    return { widths, heights };
  }, [small]);

  const closeDialog = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setDialogOpen(false);
    setPdfUrl('');
    /*
    pushValuesToRouteQuery(router, {
      pdfid: null,
    });
    */
  };

  const reportPdfFileName = useMemo(() => {
    try {
      const reportURLSearchParams = new URLSearchParams(new URL(data.reportUrl.href).search);
      return reportURLSearchParams.get('apiQueryParams');
    } catch (error) {
      console.error('Invalid report URL:', data.reportUrl.href, error);
      return null;
    }
  }, [data.reportUrl.href]);

  const viewPdf = async () => {
    if (!reportPdfFileName) return;

    setDialogOpen(true);
    const response = await getGlobalDataPdf(reportPdfFileName);
    const blob = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    setPdfUrl(url + '#page=1');

    appInsights.trackCustomEvent(TrackEventName.GLOBAL_DATA_INTEGRATION, TrackEventKey.PDF, TrackEventType.OPEN, user, {
      reportId: data.reportId,
      title: data.title,
    });
    /*
      pushValuesToRouteQuery(router, {
        pdfid: uuidv4(),
      });
      */
  };

  const downloadPdf = async () => {
    if (!reportPdfFileName) return;

    try {
      const response = await getGlobalDataPdf(reportPdfFileName);
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', data.title + '.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      appInsights.trackCustomEvent(
        TrackEventName.GLOBAL_DATA_INTEGRATION,
        TrackEventKey.PDF,
        TrackEventType.DOWNLOAD,
        user,
        {
          reportId: data.reportId,
          title: data.title,
        },
      );
    } catch (error) {
      console.error('PDF download failed:', error);
    }
  };

  const _onClick = (e: React.MouseEvent) => {
    onClick?.(e, data);
  };

  return (
    <>
      <div className={classNames(data.isLoading ? 'loading' : 'loaded', className)} {...props}>
        {data.isLoading ? (
          <div className="flex h-full flex-row gap-6 py-4">
            <div
              className={classNames(
                'flex h-full shrink-0 animate-pulse items-center justify-center bg-gray-300',
                small ? 'aspect-[80/68] w-[76px]' : 'aspect-[176/152] w-44',
              )}
            >
              <ImageIcon className="h-10 w-10 text-gray-200 dark:text-gray-600" />
            </div>
            <div className="flex w-full flex-col">
              <div className="flex justify-between">
                <div className="h-[18px] w-32 animate-pulse rounded-full bg-gray-300" />
                <div className="h-[18px] w-32 animate-pulse rounded-full bg-gray-300" />
              </div>
              <div className="mt-2 h-9 w-full animate-pulse rounded-full bg-gray-300 md:w-96" />
              {!small && <div className="mt-3 h-12 w-full animate-pulse rounded-full bg-gray-300" />}
            </div>
          </div>
        ) : (
          <div className="group relative flex h-full flex-row gap-6 py-4" onClick={_onClick}>
            <div
              className={classNames(
                'relative h-fit shrink-0',
                small ? 'aspect-[76/68] w-[76px]' : 'aspect-[176/152] w-44',
              )}
            >
              {data.image && (
                <ResponsiveImage
                  image={data.image}
                  widths={imageSize.widths}
                  heights={imageSize.heights}
                  cdnProps={{ mode: 'crop' }}
                />
              )}
              {/* Action Buttons - Hover Only */}

              <div className="buttons pointer-events-none absolute right-0 bottom-0 hidden w-full justify-end opacity-0 transition-opacity duration-500 group-hover:opacity-100 md:flex pointer-coarse:opacity-100">
                <div className="button-list">
                  <div className="flex justify-end">
                    <ReportButton
                      icon={EyeIcon}
                      text={t('general.$viewPdf')}
                      onClick={viewPdf}
                      textClassName="pr-5"
                      dark={!isImage}
                    />
                  </div>

                  <div className="flex justify-end">
                    <ReportButton
                      icon={DownloadSimpleLight}
                      text={t('general.$downloadPdf')}
                      onClick={downloadPdf}
                      textClassName="pr-5"
                      dark={!isImage}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col">
              <div className="flex justify-between text-xs leading-[18px] font-bold">
                <div>{data.directory?.name || 'GlobalData'}</div>
                <div>{dayjs(data.publishedDate)?.format('DD MMMM YYYY')}</div>
              </div>
              <h4 className="pt-2 text-sm font-light md:line-clamp-2 md:text-base lg:text-lg">{data.title}</h4>
              {!small && <div className="body-1 pt-3" dangerouslySetInnerHTML={{ __html: data.description }} />}
            </div>
          </div>
        )}
      </div>
      <Modal open={dialogOpen} onClose={closeDialog} contanerClassName="w-[90vw] h-[90vh] bg-white shadow-xl">
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-gray-300 p-4">
            <h2 className="text-lg font-medium">{data.title}</h2>
            <button onClick={closeDialog} className="btn icon">
              <XLight className="h-7 w-7" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden p-4">
            {pdfUrl && <object data={pdfUrl} type="application/pdf" className="h-full w-full"></object>}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReportListCardGD;
